import { parse, stringify } from './urlHelpers';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import { monthIdString, getStartOf } from './dates';
import { aiListingFields } from '../config/configListing';

// Note: fetchTimeSlots will be imported by the caller and passed as a parameter
// This avoids circular dependencies and keeps utilities pure

/**
 * URL parameter keys for date range
 */
export const DATE_RANGE_PARAMS = {
  START_DATE: 'startDate',
  END_DATE: 'endDate',
};

/**
 * Parse date range from URL search params
 * @param {string} search - URL search string (e.g., "?startDate=2025-09-15&endDate=2025-09-17")
 * @returns {Object|null} - { startDate: Date, endDate: Date } or null if invalid/missing
 */
// The store page's date filter is a pair of INSTANTS - midnight at the start/end day in
// the STORE's time zone (that's what its DateRangePicker produces via getStartOf, and
// what cart items get stamped with). The URL only carries calendar days, so turning one
// into an instant needs that same zone: read as browser-local midnight instead, a
// shopper whose browser sits in a different zone than the store (Vancouver shopper,
// Ottawa store - or any shared/bookmarked link) gets a filter one day off from the
// picker's own dates, cart items the panel then formats as the previous day, and a
// URL that re-parses to a different day than the one just picked. Every conversion
// between the filter and a calendar-day string below therefore takes the zone;
// without one they fall back to browser-local, which is the only meaningful choice
// for a caller that has no store (and keeps other callers' behaviour unchanged).
const calendarDayToInstant = (dayString, timeZone) =>
  timeZone
    ? moment.tz(dayString, 'YYYY-MM-DD', true, timeZone).toDate()
    : moment(dayString, 'YYYY-MM-DD', true).toDate();

const instantToCalendarDay = (date, timeZone) =>
  timeZone ? moment(date).tz(timeZone).format('YYYY-MM-DD') : moment(date).format('YYYY-MM-DD');

export const parseDateRangeFromURL = (search, timeZone) => {
  if (!search) return null;

  const params = parse(search);
  const startDateStr = params[DATE_RANGE_PARAMS.START_DATE];
  const endDateStr = params[DATE_RANGE_PARAMS.END_DATE];

  if (!startDateStr || !endDateStr) return null;

  const startDate = calendarDayToInstant(startDateStr, timeZone);
  const endDate = calendarDayToInstant(endDateStr, timeZone);

  // Validate dates
  if (!moment(startDate).isValid() || !moment(endDate).isValid()) {
    return null;
  }

  // Ensure end date is not before start date
  if (moment(endDate).isBefore(startDate, 'day')) {
    return null;
  }

  return { startDate, endDate };
};

/**
 * Update URL with date range parameters
 * @param {Object} history - React Router history object
 * @param {Date|null} startDate - Start date or null to clear
 * @param {Date|null} endDate - End date or null to clear
 */
export const updateDateRangeInURL = (history, startDate, endDate, timeZone) => {
  if (!history || !history.location) {
    console.warn('updateDateRangeInURL: history object is required');
    return;
  }

  const currentParams = parse(history.location.search);

  if (startDate && endDate) {
    // Validate dates before adding
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);

    if (!startMoment.isValid() || !endMoment.isValid()) {
      console.warn('updateDateRangeInURL: Invalid dates provided');
      return;
    }

    if (endMoment.isBefore(startMoment, 'day')) {
      console.warn(
        'updateDateRangeInURL: End date cannot be before start date'
      );
      return;
    }

    currentParams[DATE_RANGE_PARAMS.START_DATE] = instantToCalendarDay(startDate, timeZone);
    currentParams[DATE_RANGE_PARAMS.END_DATE] = instantToCalendarDay(endDate, timeZone);
  } else {
    // Remove date range params if dates are cleared
    delete currentParams[DATE_RANGE_PARAMS.START_DATE];
    delete currentParams[DATE_RANGE_PARAMS.END_DATE];
  }

  const newSearch = stringify(currentParams);
  history.replace({ search: newSearch ? `?${newSearch}` : '' });
};

/**
 * Format date for display in date picker inputs
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date string (e.g., "Sep 15, 2025")
 */
export const formatDateForDisplay = date => {
  if (!date || !moment(date).isValid()) return '';
  return moment(date).format('MMM D, YYYY');
};

/**
 * Format date range for compact display (e.g., "Sep 12, 26 – Sep 16, 26")
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {string} - Formatted date range string
 */
export const formatDateRangeCompact = (startDate, endDate) => {
  if (
    !startDate ||
    !endDate ||
    !moment(startDate).isValid() ||
    !moment(endDate).isValid()
  )
    return '';
  return `${moment(startDate).format('MMM D, YY')} – ${moment(endDate).format(
    'MMM D, YY'
  )}`;
};

/**
 * Convert an exclusive booking end date (the day after the last rental day,
 * per Sharetribe API convention) back to the inclusive last day for display.
 * @param {Date} endDate - Exclusive end date
 * @returns {Date|null} - Inclusive end date, or null if invalid
 */
export const getInclusiveEndDate = (endDate, timeZone) => {
  if (!endDate || !moment(endDate).isValid()) return null;
  const m = timeZone ? moment(endDate).tz(timeZone) : moment(endDate);
  return m.subtract(1, 'days').toDate();
};

/**
 * Format date range for compact display (e.g., "Sep 12, 26 – Sep 16, 26")
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {string} [timeZone] - zone the dates are calendar days in (the store's);
 *   browser-local when omitted
 * @returns {string} - Formatted date range string
 */
export const formatDateRangeCompactShort = (startDate, endDate, timeZone) => {
  if (!startDate || !endDate || !moment(startDate).isValid() || !moment(endDate).isValid()) return '';
  const fmt = date => (timeZone ? moment(date).tz(timeZone) : moment(date)).format('MMM D, YY');
  return `${fmt(startDate)} – ${fmt(endDate)}`;
};

/**
 * Format date for date picker placeholder
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date string (e.g., "Select start date")
 */
export const formatDatePlaceholder = (type = 'start') => {
  return type === 'start' ? 'Select start date' : 'Select end date';
};

/**
 * Check if a date range is valid
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return false;

  const startMoment = moment(startDate);
  const endMoment = moment(endDate);

  if (!startMoment.isValid() || !endMoment.isValid()) return false;
  if (endMoment.isBefore(startMoment, 'day')) return false;

  return true;
};

// ================ Availability Helpers ================ //

/**
 * Get array of month IDs needed for a date range
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {string} timeZone - Timezone (defaults to 'UTC')
 * @returns {Array<string>} - Array of month IDs like ["2025-09", "2025-10"]
 */
export const getMonthsForDateRange = (startDate, endDate, timeZone = 'UTC') => {
  if (!startDate || !endDate) return [];

  const months = [];
  const start = moment(startDate).tz(timeZone);
  const end = moment(endDate).tz(timeZone);
  const current = start.clone().startOf('month');

  while (current.isSameOrBefore(end, 'month')) {
    months.push(monthIdString(current.toDate(), timeZone));
    current.add(1, 'month');
  }

  return months;
};

/**
 * Check which months need to be fetched (not already in Redux)
 * @param {Array<string>} neededMonths - Array of month IDs needed
 * @param {Object} monthlyTimeSlots - Redux monthlyTimeSlots state
 * @returns {Array<string>} - Array of month IDs that need fetching
 */
export const getMonthsToFetch = (neededMonths, monthlyTimeSlots) => {
  if (!neededMonths || neededMonths.length === 0) return [];
  if (!monthlyTimeSlots) return neededMonths;

  return neededMonths.filter(monthId => {
    const monthData = monthlyTimeSlots[monthId];
    // Fetch if: not exists, or exists but no timeSlots and not currently fetching
    return !monthData?.timeSlots && !monthData?.fetchTimeSlotsInProgress;
  });
};

/**
 * Fetch time slots for months needed for a date range
 * Only fetches months that aren't already in Redux
 *
 * @param {Function} dispatch - Redux dispatch
 * @param {Function} getState - Redux getState
 * @param {Function} fetchTimeSlotsAction - Redux thunk action creator for fetching time slots
 * @param {string} listingId - Listing UUID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {string} timeZone - Timezone (defaults to 'UTC')
 * @returns {Promise} - Promise that resolves when all needed months are fetched
 */
export const fetchTimeSlotsForDateRange = (
  dispatch,
  getState,
  fetchTimeSlotsAction,
  listingId,
  startDate,
  endDate,
  timeZone = 'UTC'
) => {
  if (
    !startDate ||
    !endDate ||
    !listingId ||
    !dispatch ||
    !getState ||
    !fetchTimeSlotsAction
  ) {
    return Promise.resolve();
  }

  // Get months needed for this date range
  const neededMonths = getMonthsForDateRange(startDate, endDate, timeZone);

  if (neededMonths.length === 0) {
    return Promise.resolve();
  }

  // Check Redux to see which months are already fetched
  const state = getState();
  const monthlyTimeSlots = state?.ListingPage?.monthlyTimeSlots || {};
  const monthsToFetch = getMonthsToFetch(neededMonths, monthlyTimeSlots);

  if (monthsToFetch.length === 0) {
    // All months already fetched!
    return Promise.resolve();
  }

  // Fetch each missing month
  const fetchPromises = monthsToFetch.map(monthId => {
    // Convert monthId "2025-09" to start/end dates for that month
    const monthStart = moment(monthId, 'YYYY-MM')
      .tz(timeZone)
      .startOf('month')
      .toDate();
    const monthEnd = getStartOf(monthStart, 'month', timeZone, 1, 'months');

    return dispatch(
      fetchTimeSlotsAction(listingId, monthStart, monthEnd, timeZone)
    );
  });

  return Promise.all(fetchPromises);
};

// ================ Category Filter Helpers ================ //

/**
 * URL parameter keys for category filter
 */
export const CATEGORY_PARAMS = {
  CATEGORY_ID: 'categoryId',
  CATEGORY_DEPTH: 'categoryDepth',
};

/**
 * URL parameter key for search query
 */
export const SEARCH_QUERY_PARAM = 'q';
export const STORE_CART_QUERY_PARAM = 'storeCart';
export const STORE_CART_OPEN_VALUE = 'open';
export const LISTING_FILTER_QUERY_KEYS = [
  'category',
  'access_to_rentals',
  'Condition_of_the_item',
  'Trust_and_verification',
  'cancelation_policy',
  'reason_for_listing',
  'security_deposit',
  'priceRange',
];

export const parseListingFacetFiltersFromURL = search => {
  if (!search) return {};
  const params = parse(search);

  return LISTING_FILTER_QUERY_KEYS.reduce((acc, key) => {
    const value = params[key];
    if (value !== undefined && value !== null && `${value}`.length > 0) {
      acc[key] = `${value}`;
    }
    return acc;
  }, {});
};

/**
 * Parse category filter from URL search params
 * @param {string} search - URL search string (e.g., "?categoryId=abc123&categoryDepth=1")
 * @returns {Object|null} - { categoryId: string, depth: number } or null if invalid/missing
 */
export const parseCategoryFromURL = search => {
  if (!search) return null;

  const params = parse(search);
  const categoryId = params[CATEGORY_PARAMS.CATEGORY_ID] || null;
  const categoryDepth =
    params[CATEGORY_PARAMS.CATEGORY_DEPTH] >= 0 &&
      params[CATEGORY_PARAMS.CATEGORY_DEPTH] !== undefined
      ? parseInt(params[CATEGORY_PARAMS.CATEGORY_DEPTH], 10)
      : null;

  // Validate depth is 0..4 (0 = marketplace-only, 1-4 = store categories matching lvl0..lvl3)
  if (
    categoryId &&
    categoryDepth !== null &&
    [0, 1, 2, 3, 4].includes(categoryDepth)
  ) {
    return { categoryId, depth: categoryDepth };
  }

  return null;
};

/**
 * Update URL with category filter parameters
 * @param {Object} history - React Router history object
 * @param {Object} location - React Router location object
 * @param {Object|null} category - { categoryId: string, depth: number } or null to clear
 */
export const updateCategoryInURL = (history, location, category) => {
  if (!history || !location) {
    console.warn(
      'updateCategoryInURL: history and location objects are required'
    );
    return;
  }

  const currentParams = parse(location.search || '');

  // depth can be 0 (marketplace-only root). Treat 0 as valid.
  if (category?.categoryId && category?.depth >= 0) {
    currentParams[CATEGORY_PARAMS.CATEGORY_ID] = category.categoryId;
    currentParams[CATEGORY_PARAMS.CATEGORY_DEPTH] = category.depth.toString();
  } else {
    delete currentParams[CATEGORY_PARAMS.CATEGORY_ID];
    delete currentParams[CATEGORY_PARAMS.CATEGORY_DEPTH];
  }

  const newSearch = stringify(currentParams);
  history.replace({
    pathname: location.pathname,
    search: newSearch ? `?${newSearch}` : '',
    hash: location.hash,
  });
};

/**
 * Parse search query from URL search params
 * @param {string} search - URL search string (e.g., "?q=search+term")
 * @returns {string|null} - Search query string or null if missing
 */
export const parseSearchQueryFromURL = search => {
  if (!search) return null;
  const params = parse(search);
  const query = params[SEARCH_QUERY_PARAM];
  return query &&
    (typeof query === 'string' || typeof query === 'number') &&
    String(query).trim().length > 0
    ? String(query).trim()
    : null;
};

/**
 * Parse both category and date range filters from URL
 * @param {string} search - URL search string
 * @returns {Object} - { category: Object|null, dateRange: Object|null, searchQuery: string|null }
 */
export const parseFiltersFromURL = (search, timeZone) => {
  return {
    category: parseCategoryFromURL(search),
    dateRange: parseDateRangeFromURL(search, timeZone),
    searchQuery: parseSearchQueryFromURL(search),
    listingFacetFilters: parseListingFacetFiltersFromURL(search),
  };
};

/**
 * Update URL with both category and date range filters
 * @param {Object} history - React Router history object
 * @param {Object} location - React Router location object
 * @param {Object} filters - { category: Object|null, dateRange: Object|null, searchQuery: string|null }
 */
export const updateFiltersInURL = (history, location, filters, timeZone) => {
  if (!history || !location) {
    console.warn(
      'updateFiltersInURL: history and location objects are required'
    );
    return;
  }

  const currentParams = parse(location.search || '');

  // Update category params
  if (filters.category?.categoryId && filters.category?.depth >= 0) {
    currentParams[CATEGORY_PARAMS.CATEGORY_ID] = filters.category.categoryId;
    currentParams[
      CATEGORY_PARAMS.CATEGORY_DEPTH
    ] = filters.category.depth.toString();
  } else {
    delete currentParams[CATEGORY_PARAMS.CATEGORY_ID];
    delete currentParams[CATEGORY_PARAMS.CATEGORY_DEPTH];
  }

  // Update date range params
  if (filters.dateRange?.startDate && filters.dateRange?.endDate) {
    const startMoment = moment(filters.dateRange.startDate);
    const endMoment = moment(filters.dateRange.endDate);

    if (
      startMoment.isValid() &&
      endMoment.isValid() &&
      !endMoment.isBefore(startMoment, 'day')
    ) {
      currentParams[DATE_RANGE_PARAMS.START_DATE] = instantToCalendarDay(
        filters.dateRange.startDate,
        timeZone
      );
      currentParams[DATE_RANGE_PARAMS.END_DATE] = instantToCalendarDay(
        filters.dateRange.endDate,
        timeZone
      );
    } else {
      delete currentParams[DATE_RANGE_PARAMS.START_DATE];
      delete currentParams[DATE_RANGE_PARAMS.END_DATE];
    }
  } else {
    delete currentParams[DATE_RANGE_PARAMS.START_DATE];
    delete currentParams[DATE_RANGE_PARAMS.END_DATE];
  }

  // Update search query param
  if (
    filters.searchQuery &&
    typeof filters.searchQuery === 'string' &&
    filters.searchQuery.trim().length > 0
  ) {
    currentParams[SEARCH_QUERY_PARAM] = filters.searchQuery.trim();
  } else {
    delete currentParams[SEARCH_QUERY_PARAM];
  }

  // Update listing facet filters
  LISTING_FILTER_QUERY_KEYS.forEach(key => {
    const value = filters?.listingFacetFilters?.[key];
    if (value !== undefined && value !== null && `${value}`.length > 0) {
      currentParams[key] = `${value}`;
    } else {
      delete currentParams[key];
    }
  });

  const newSearch = stringify(currentParams);
  history.replace({
    pathname: location.pathname,
    search: newSearch ? `?${newSearch}` : '',
    hash: location.hash,
  });
};

/**
 * Build a stable key for MainContent that excludes params that should not remount.
 * Excludes: category (preserves sidebar scroll), search query (preserves Sidebar
 * sticky/keyboard position when InstantSearch triggers re-renders after search),
 * date range (timeslots re-fetch via useListingAvailability props — no remount needed),
 * storeCart (cart modal open/close must not remount the listing grid).
 */
export const getMainContentKeyFromSearch = search => {
  if (!search) return '';
  const params = parse(search || '');
  delete params[CATEGORY_PARAMS.CATEGORY_ID];
  delete params[CATEGORY_PARAMS.CATEGORY_DEPTH];
  delete params[SEARCH_QUERY_PARAM];
  delete params[DATE_RANGE_PARAMS.START_DATE];
  delete params[DATE_RANGE_PARAMS.END_DATE];
  delete params[STORE_CART_QUERY_PARAM];
  LISTING_FILTER_QUERY_KEYS.forEach(key => {
    delete params[key];
  });
  const s = stringify(params);
  return s || '';
};


export const parseStoreCartFromURL = search => {
  if (!search) return false;
  const params = parse(search);
  return params[STORE_CART_QUERY_PARAM] === STORE_CART_OPEN_VALUE;
};

export const getSearchWithStoreCart = (search = '', isOpen = true) => {
  const currentParams = parse(search || '');

  if (isOpen) {
    currentParams[STORE_CART_QUERY_PARAM] = STORE_CART_OPEN_VALUE;
  } else {
    delete currentParams[STORE_CART_QUERY_PARAM];
  }

  const newSearch = stringify(currentParams);
  return newSearch ? `?${newSearch}` : '';
};

/**
 * Opens the Store Cart panel from anywhere on a storefront route (nav-bar cart
 * buttons) by pushing ?storeCart=open - a push, not a replace, so the browser back
 * button closes the panel. No-op when the panel is already open: re-tapping the cart
 * button used to push an identical entry each time, so Back then had to be pressed
 * once per tap before it did anything visible.
 */
export const openStoreCartInURL = (history, location) => {
  if (!history || !location) return;
  if (parseStoreCartFromURL(location.search || '')) return;
  // The topbar's mobile menu is itself URL-driven (?mobilemenu=open) and would
  // otherwise stay open on top of the cart when the cart is opened from inside it.
  const params = parse(location.search || '');
  delete params.mobilemenu;
  const searchWithoutMenu = stringify(params);
  history.push({
    pathname: location.pathname,
    search: getSearchWithStoreCart(searchWithoutMenu ? `?${searchWithoutMenu}` : '', true),
    hash: location.hash,
  });
};

export const updateStoreCartInURL = (history, location, isOpen = true) => {
  if (!history || !location) {
    console.warn(
      'updateStoreCartInURL: history and location objects are required'
    );
    return;
  }

  history.replace({
    pathname: location.pathname,
    search: getSearchWithStoreCart(location.search, isOpen),
    hash: location.hash,
  });
};

// ================ Algolia Hierarchical Menu Mapping Helpers ================ //

/**
 * Get category depth (1-3)
 * @param {string} categoryId - Category ID
 * @param {Array} categories - Store categories array
 * @returns {number} - Depth (1-3)
 */
export const getCategoryDepthFromId = (categoryId, categories) => {
  const idToNode = new Map(categories.map(c => [c.id, c]));
  const category = idToNode.get(categoryId);
  if (!category) return 0;

  let depth = 1;
  let current = category;

  while (current) {
    const parentId = current.parentId;
    if (!parentId) break;
    if (parentId.startsWith('mp:')) break;
    if (parentId.startsWith('sc:')) {
      depth += 1;
      const parentStoreId = parentId.slice(3);
      current = idToNode.get(parentStoreId);
    } else {
      break;
    }
  }

  return Math.min(depth, 4); // Cap at 4 (matches lvl0..lvl3)
};

const getMarketplaceLabelByOption = (option, locale = 'en') => {
  if (!option) return null;
  try {
    const field = (aiListingFields || []).find(f => f.key === 'category');
    if (!field) {
      console.warn(
        'getMarketplaceLabelByOption: category field not found in aiListingFields'
      );
      return null;
    }
    const opts = field?.enumOptions || [];
    const match = opts.find(o => o.option === option);
    if (!match) {
      console.warn(
        `getMarketplaceLabelByOption: option "${option}" not found in marketplace categories`
      );
      return null;
    }
    const labels = match.labels || {};
    // Try locale-specific label first, then fallback to default label, then option itself
    return labels[locale] || match.label || option;
  } catch (error) {
    console.error(
      'getMarketplaceLabelByOption: error getting marketplace label',
      error
    );
    return null;
  }
};

/**
 * Map marketplace label to option (reverse of getMarketplaceLabelByOption)
 * @param {string} label - Marketplace label (e.g., "Party & Events")
 * @param {string} locale - Locale code (defaults to 'en')
 * @returns {string|null} - Marketplace option (e.g., "party") or null
 */
const getMarketplaceOptionByLabel = (label, locale = 'en') => {
  if (!label) return null;
  try {
    const field = (aiListingFields || []).find(f => f.key === 'category');
    if (!field) {
      console.warn(
        'getMarketplaceOptionByLabel: category field not found in aiListingFields'
      );
      return null;
    }
    const opts = field?.enumOptions || [];

    // Try exact match first (locale-specific label)
    for (const opt of opts) {
      const labels = opt.labels || {};
      const optLabel = labels[locale] || opt.label;
      if (optLabel === label) return opt.option;
    }

    // Fallback: try matching against default label (for cases where locale changed)
    for (const opt of opts) {
      if (opt.label === label) return opt.option;
    }

    return null;
  } catch (error) {
    console.error(
      'getMarketplaceOptionByLabel: error getting marketplace option',
      error
    );
    return null;
  }
};

/**
 * Build category path string from categoryId
 * @param {string} categoryId - Category ID
 * @param {Array} categories - Store categories array
 * @param {Object} groups - Store category groups
 * @param {string} locale - Locale code
 * @param {Function} getName - Function to get category name
 * @returns {string} - Hierarchical path string
 */
const buildCategoryPath = (categoryId, categories, groups, locale, getName) => {
  const idToNode = new Map(categories.map(c => [c.id, c]));
  const category = idToNode.get(categoryId);
  if (!category) return '';

  const path = [];
  let current = category;

  // Build path from leaf to root
  while (current) {
    const name = getName(current);
    if (name) {
      path.unshift(name);
    }

    const parentId = current.parentId;
    if (!parentId) break;

    if (parentId.startsWith('mp:')) {
      // Marketplace parent - include marketplace label at lvl0 if visible
      const mpVisible = groups?.[parentId]?.visible !== false;
      if (mpVisible) {
        const mpOption = parentId.slice(3);
        const mpLabel = getMarketplaceLabelByOption(mpOption, locale);
        if (mpLabel) path.unshift(mpLabel);
      }
      break;
    }

    const parentStoreId = parentId.startsWith('sc:')
      ? parentId.slice(3)
      : parentId;
    current = idToNode.get(parentStoreId);
    if (!current) break;
  }

  return path.join(' > ');
};

/**
 * Map Algolia hierarchical value to categoryId and depth
 * @param {string} hierarchicalValue - e.g., "Party & Events > Tables"
 * @param {Object} user - User object with storeCategories
 * @param {string} locale - Locale code (defaults to 'en')
 * @returns {Object|null} - { categoryId: string, depth: number } or null
 */
export const mapHierarchicalValueToCategoryId = (
  hierarchicalValue,
  user,
  locale = 'en'
) => {
  if (!hierarchicalValue || !user) return null;

  const storeCategories =
    user?.attributes?.profile?.publicData?.storeCategories || [];
  const storeCategoryGroups =
    user?.attributes?.profile?.publicData?.storeCategoryGroups || {};

  // Split hierarchical value into parts
  const parts = hierarchicalValue.split(' > ').filter(Boolean);
  if (parts.length === 0) return null;

  // Check if it's a marketplace-only category (just one part, and it matches a marketplace label)
  if (parts.length === 1) {
    const mpOption = getMarketplaceOptionByLabel(parts[0], locale);
    if (mpOption) {
      // Check if this marketplace has visible store categories (if not, it shouldn't appear in hierarchy)
      const mpKey = `mp:${mpOption}`;
      // const mpVisible = storeCategoryGroups?.[mpKey]?.visible !== false;
      // if (mpVisible) {
      return {
        categoryId: mpKey, // e.g., "mp:party"
        depth: 0, // Special depth for marketplace-only
        parentId: null,
      };
      // }
    }
  }

  // If no store categories, can't match store category paths
  if (!storeCategories.length) return null;

  // Build lookup maps
  const idToNode = new Map(storeCategories.map(c => [c.id, c]));
  const getName = node => {
    if (!node) return null;
    const names = node.names || {};
    return names[locale] || node.name || null;
  };

  // Match by store category names
  // Find categories that match the last part of the hierarchy
  const lastPart = parts[parts.length - 1];

  for (const category of storeCategories) {
    const categoryName = getName(category);
    if (categoryName === lastPart) {
      // Check if the full path matches
      const categoryPath = buildCategoryPath(
        category.id,
        storeCategories,
        storeCategoryGroups,
        locale,
        getName
      );
      // Algolia values may include marketplace label at the front; accept suffix match.
      if (
        categoryPath === hierarchicalValue ||
        hierarchicalValue.endsWith(categoryPath)
      ) {
        const depth = getCategoryDepthFromId(category.id, storeCategories);
        return {
          categoryId: category.id,
          depth: depth,
          parentId: category.parentId,
        };
      }
    }
  }

  return null;
};

/**
 * Map categoryId/depth to Algolia hierarchical value
 * @param {string} categoryId - Category ID
 * @param {number} depth - Category depth (1-3)
 * @param {Object} user - User object
 * @param {string} locale - Locale code (defaults to 'en')
 * @returns {string|null} - Hierarchical value or null
 */
export const mapCategoryIdToHierarchicalValue = (
  categoryId,
  depth,
  user,
  locale = 'en'
) => {
  if (!categoryId || !user) return null;

  // Handle marketplace-only categories (mp:option format with depth 0)
  if (categoryId.startsWith('mp:') && depth === 0) {
    const mpOption = categoryId.slice(3);
    const mpLabel = getMarketplaceLabelByOption(mpOption, locale);
    return mpLabel || null;
  }

  // Handle store categories (require depth > 0)
  if (!depth) return null;

  const storeCategories =
    user?.attributes?.profile?.publicData?.storeCategories || [];
  const storeCategoryGroups =
    user?.attributes?.profile?.publicData?.storeCategoryGroups || {};

  if (!storeCategories.length) return null;

  const getName = node => {
    if (!node) return null;
    const names = node.names || {};
    return names[locale] || node.name || null;
  };

  return buildCategoryPath(
    categoryId,
    storeCategories,
    storeCategoryGroups,
    locale,
    getName
  );
};
